import { useMemo } from 'react';
import Text from '@ui/text/text/text';
import SettingsToggle from '../../controls/settings-toggle';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectSessionByField } from '@/store/slices/project';
import { getWorkingProjectSessions } from '@/store/slices/project/selectors';
import Container from '@ui/container/container';

const ModerationSettings = () => {
  const sessions = useAppSelector(getWorkingProjectSessions);
  const session = sessions?.[0];
  const dispatch = useAppDispatch();

  const moderation = useMemo(
    () => session?.moderation ?? { posts: false, questions: false, chat: false },
    [session?.moderation]
  );
  const chatContent = useMemo(() => session?.chat_content_settings ?? {}, [session?.chat_content_settings]);
  const sessionUuid = session?.uuid;

  const handleSessionChat = (_: string, on: boolean) => {
    if (!sessionUuid) return;
    dispatch(
      saveWorkingProjectSessionByField({
        uuid: sessionUuid,
        session_chat_enabled: on,
      })
    );
  };

  const handleQaResponses = (_: string, on: boolean) => {
    if (!sessionUuid) return;
    dispatch(
      saveWorkingProjectSessionByField({
        uuid: sessionUuid,
        moderation: { ...moderation, questions: on },
      })
    );
  };

  const handleAllowImages = (_: string, on: boolean) => {
    if (!sessionUuid) return;
    dispatch(
      saveWorkingProjectSessionByField({
        uuid: sessionUuid,
        chat_content_settings: { ...chatContent, disable_images: !on },
      })
    );
  };

  const handleAllowGifs = (_: string, on: boolean) => {
    if (!sessionUuid) return;
    dispatch(
      saveWorkingProjectSessionByField({
        uuid: sessionUuid,
        chat_content_settings: { ...chatContent, disable_gifs: !on },
      })
    );
  };

  const handleAllowEmojis = (_: string, on: boolean) => {
    if (!sessionUuid) return;
    dispatch(
      saveWorkingProjectSessionByField({
        uuid: sessionUuid,
        chat_content_settings: { ...chatContent, disable_emojis: !on },
      })
    );
  };

  return (
    <Container className="flex-column gap-8">
      <Container className="flex">
        <Text className="H5" color="var(--gray-100)">
          Moderation Options
        </Text>
      </Container>
      <SettingsToggle
        label="Session Chat"
        sKey="session_chat"
        value={!!session?.session_chat_enabled}
        disabled={!sessionUuid}
        onToggle={handleSessionChat}
        tooltipText="Requires a moderator to approve each chat message before showing to audience"
      />
      <SettingsToggle
        label="QA Responses"
        sKey="qa_responses"
        value={!!moderation.questions}
        disabled={!sessionUuid}
        onToggle={handleQaResponses}
        tooltipText="Requires a moderator to approve each question before showing to audience"
      />
      <SettingsToggle
        label="Allow images"
        sKey="allow_images"
        value={chatContent.disable_images !== true}
        disabled={!sessionUuid}
        onToggle={handleAllowImages}
        tooltipText="Allow images in the chat"
      />
      <SettingsToggle
        label="Allow GIFs"
        sKey="allow_gifs"
        value={chatContent.disable_gifs !== true}
        disabled={!sessionUuid}
        onToggle={handleAllowGifs}
        tooltipText="Allow GIFs in the chat"
      />
      <SettingsToggle
        label="Allow Emojis"
        sKey="allow_emojis"
        value={chatContent.disable_emojis !== true}
        disabled={!sessionUuid}
        onToggle={handleAllowEmojis}
        tooltipText="Allow emojis in the chat"
      />
    </Container>
  );
};

export default ModerationSettings;
